import {
  BoundNumberField,
  column,
  Css,
  emptyCell,
  FormLines,
  GridColumn,
  GridDataRow,
  GridTable,
  numericColumn,
  ScrollableContent,
  SelectField,
  simpleHeader,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, useFormStates } from "@homebound/form-state";
import { emptyCellDash, priceTotal, tagCell } from "src/components";
import { ChangeEventReadyPlanOptionInput, ProjectReadyPlanConfigOrientation } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import {
  ChangeEventRposTab_AddedReadyPlanOptionFragment,
  ChangeEventRposTab_RemovedReadyPlanOptionFragment,
  ChangeEventRposTabQuery,
  useChangeEventRposTabQuery,
  useSaveChangeEventRposMutation,
} from "src/routes/projects/change-events/ChangeEventRposTab.generated";
import { ChangeEventParams } from "src/routes/routesDef";
import { queryResult } from "src/utils";

export function ChangeEventRposTab() {
  const { changeEventId } = useRouteParams<ChangeEventParams>();
  const query = useChangeEventRposTabQuery({ variables: { id: changeEventId } });
  return queryResult(query, (data) => <ChangeEventReadyPlanOptionsTable changeEvent={data.changeEvent} />);
}

type ReadyPlanOptionsTableProps = { changeEvent: ChangeEventRposTabQuery["changeEvent"] };

const orientationOptions = [
  { code: ProjectReadyPlanConfigOrientation.Left, name: "Left" },
  { code: ProjectReadyPlanConfigOrientation.Right, name: "Right" },
];

function ChangeEventReadyPlanOptionsTable({ changeEvent }: ReadyPlanOptionsTableProps) {
  const [saveChangeEventRpos] = useSaveChangeEventRposMutation();
  const { getFormState } = useFormStates<ChangeEventReadyPlanOptionInput, RpoData>({
    config: formConfig,
    getId: (v) => v.id,
    autoSave: async (os) => {
      const updated = os.value;
      // Check if this is a proposed/original option
      const isAdded = changeEvent.addedReadyPlanOptions.some((opt) => opt.id === updated.id);
      await saveChangeEventRpos({
        variables: {
          input: {
            id: changeEvent.id,
            ...(isAdded ? { proposedReadyPlanOptions: [updated] } : { originalReadyPlanOptions: [updated] }),
          },
        },
      });
    },
    readOnly: !changeEvent.canEditLineItems.allowed,
  });
  const rows = createRows(changeEvent);
  const columns = createColumns(getFormState);
  const readOnly = !changeEvent.canEditLineItems.allowed;

  async function saveOrientation(orientation: ProjectReadyPlanConfigOrientation | undefined) {
    await saveChangeEventRpos({ variables: { input: { id: changeEvent.id, proposedOrientation: orientation } } });
  }

  return (
    <ScrollableContent>
      <div css={Css.mb2.$}>
        <FormLines width="sm">
          <SelectField
            label="Orientation"
            value={changeEvent.proposedOrientation?.code}
            onSelect={(value) => saveOrientation(value)}
            options={orientationOptions}
            getOptionValue={(o) => o.code}
            getOptionLabel={(o) => o.name}
            readOnly={readOnly}
            unsetLabel="None"
          />
        </FormLines>
      </div>
      <GridTable
        columns={columns}
        rows={rows}
        fallbackMessage="This Change Event has no Ready Plan Option changes"
        sorting={{ on: "client" }}
        stickyHeader
        style={{ bordered: true, allWhite: true }}
      />
    </ScrollableContent>
  );
}

type RpoData = (ChangeEventRposTab_RemovedReadyPlanOptionFragment | ChangeEventRposTab_AddedReadyPlanOptionFragment) & {
  type: "added" | "removed";
};
type HeaderRow = { kind: "header" };
type TotalsRow = { kind: "totals"; data: { totalPriceInCents: number } };
type RpoRow = { kind: "rpo"; id: string; data: RpoData };
type Row = HeaderRow | TotalsRow | RpoRow;

const formConfig: ObjectConfig<ChangeEventReadyPlanOptionInput> = {
  id: { type: "value" },
  priceInCents: { type: "value" },
  readyPlanOptionId: { type: "value" },
};

function createColumns(
  getFormState: (row: RpoData) => ObjectState<ChangeEventReadyPlanOptionInput>,
): GridColumn<Row>[] {
  return [
    column<Row>({
      header: "Option Code",
      rpo: ({ readyPlanOption }) => readyPlanOption.code,
      totals: () => ({ content: "Totals", colspan: 4 }),
    }),
    column<Row>({ header: "Option Name", totals: "", rpo: ({ readyPlanOption }) => readyPlanOption.name, w: 2 }),
    column<Row>({
      header: "Location",
      rpo: ({ readyPlanOption }) => readyPlanOption.location?.name ?? emptyCellDash,
      totals: emptyCell,
    }),
    column<Row>({
      header: "Type",
      rpo: ({ type }) => (type === "added" ? tagCell("success", "Added") : tagCell("update", "Removed")),
      totals: emptyCell,
      w: "125px",
    }),
    numericColumn<Row>({
      header: "Price",
      rpo: (data) => {
        const os = getFormState(data);
        return <BoundNumberField type="cents" displayDirection field={os.priceInCents} />;
      },
      totals: (data) => priceTotal({ valueInCents: data.totalPriceInCents, displayDirection: true, id: "rpoTotal" }),
      w: "150px",
      clientSideSort: false,
    }),
  ];
}

function createRows(changeEvent: ChangeEventRposTabQuery["changeEvent"]): GridDataRow<Row>[] {
  const allOptions = [...changeEvent.addedReadyPlanOptions, ...changeEvent.removedReadyPlanOptions];
  const totalPriceInCents = allOptions.reduce((sum, opt) => sum + (opt.priceInCents ?? 0), 0);

  return [
    simpleHeader,
    ...changeEvent.addedReadyPlanOptions.map((opt) => ({
      kind: "rpo" as const,
      id: opt.id,
      data: { ...opt, type: "added" as const },
    })),
    ...changeEvent.removedReadyPlanOptions.map((opt) => ({
      kind: "rpo" as const,
      id: opt.id,
      data: { ...opt, type: "removed" as const },
    })),
    { kind: "totals" as const, id: "totals", data: { totalPriceInCents } },
  ];
}
